import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Timer, Copy } from 'lucide-react';
import {
  endSubathon,
  getMySubathon,
  pauseSubathon,
  resumeSubathon,
  startSubathon,
  Subathon,
} from '../../../api/payment/subathon.api';
import { clockSkewMs, formatAdd, formatHms, remainingAt } from '../../../utils/subathon';
import { parseServerDate } from '../../../utils/server-date';
import usePollWhile from '../../../hooks/use-poll-while/use-poll-while';

// "Đếm ngược subathon": streamer đặt thời lượng ban đầu + tỉ lệ "cứ X Ca thêm 1 phút" rồi chèn
// đồng hồ đếm ngược lên phiên live bằng link overlay (OBS Browser Source). Mỗi lượt donate cộng
// thêm giờ theo tỉ lệ, có trần tổng thời lượng; đang chạy thì khoá tỉ lệ/thời lượng ban đầu/trần
// (chỉ đổi được bằng cách kết thúc rồi bắt đầu lại).

const BASE_MINUTES_MIN = 1;
const BASE_MINUTES_MAX = 1440; // 86400s / 60
const MAX_HOURS_MIN = 1;
const MAX_HOURS_MAX = 168; // 604800s / 3600 = 7 ngày
const RELOAD_MS = 30_000;

/** Ép số nguyên ngay lúc gõ (làm tròn, kẹp khoảng cho phép) — ô trống vẫn cho phép trống tạm. */
const toIntString = (raw: string, min?: number, max?: number): string => {
  if (raw === '') return '';
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return '';
  let clamped = n;
  if (min !== undefined) clamped = Math.max(min, clamped);
  if (max !== undefined) clamped = Math.min(max, clamped);
  return `${clamped}`;
};

interface StartForm {
  title: string;
  baseMinutes: string;
  caPerMinute: string;
  maxHours: string;
}

const EMPTY_FORM: StartForm = { title: '', baseMinutes: '30', caPerMinute: '', maxHours: '12' };

const SubathonSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const { host, protocol } = window.location;
  const overlayUrl = `${protocol}//${host}/subathon-screen/${userId}`;

  const [sub, setSub] = useState<Subathon | null>(null);
  const [form, setForm] = useState<StartForm>(EMPTY_FORM);
  const [now, setNow] = useState(() => Date.now());
  const [skewMs, setSkewMs] = useState(0);
  const [starting, setStarting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [ending, setEnding] = useState(false);
  const zeroReloadedRef = useRef(false);

  const load = () => {
    getMySubathon()
      .then((res) => {
        setSub(res);
        setSkewMs(clockSkewMs(res.serverNow));
        zeroReloadedRef.current = false;
      })
      .catch(() => {
        // Chưa từng bắt đầu / lỗi mạng -> để form trống, không chặn thao tác bắt đầu mới.
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Đang chạy: đang diễn ra (kể cả tạm dừng) -> đồng hồ 1 giây (chỉ cần tick khi đang chạy thật
  // sự để hiện số giảm dần) + tải lại dự phòng mỗi 30 giây để bắt kịp trạng thái đổi từ nơi khác
  // (vd BE tự lưu Ended khi hết giờ tự nhiên).
  const isActive = !!sub?.exists && sub.status !== 'Ended';
  const isCounting = sub?.status === 'Running' && !sub.isPaused;
  usePollWhile(isCounting, () => setNow(Date.now()), 1000);
  usePollWhile(isActive, load, RELOAD_MS);

  const handleStart = () => {
    if (starting) return; // chặn bấm 2 lần
    const title = form.title.trim();
    if (!title) {
      toast.error('Vui lòng nhập tên subathon.');
      return;
    }
    const baseMinutes = Number(form.baseMinutes);
    if (!baseMinutes || baseMinutes < BASE_MINUTES_MIN || baseMinutes > BASE_MINUTES_MAX) {
      toast.error(`Thời lượng ban đầu phải từ ${BASE_MINUTES_MIN} đến ${BASE_MINUTES_MAX} phút.`);
      return;
    }
    const caPerMinute = Number(form.caPerMinute);
    if (!caPerMinute || caPerMinute < 1) {
      toast.error('Số Ca mỗi phút phải lớn hơn 0.');
      return;
    }
    const maxHours = Number(form.maxHours);
    if (!maxHours || maxHours < MAX_HOURS_MIN || maxHours > MAX_HOURS_MAX) {
      toast.error(`Trần thời lượng phải từ ${MAX_HOURS_MIN} đến ${MAX_HOURS_MAX} giờ.`);
      return;
    }
    if (maxHours * 60 < baseMinutes) {
      toast.error('Trần thời lượng phải lớn hơn hoặc bằng thời lượng ban đầu.');
      return;
    }

    setStarting(true);
    startSubathon({ title, baseMinutes, caPerMinute, maxHours })
      .then((res) => {
        setSub(res);
        setSkewMs(clockSkewMs(res.serverNow));
        setNow(Date.now());
        toast.success('Đã bắt đầu subathon');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Bắt đầu subathon thất bại');
      })
      .finally(() => setStarting(false));
  };

  const handleTogglePause = () => {
    if (toggling || !sub) return; // chặn bấm 2 lần
    setToggling(true);
    const action = sub.isPaused ? resumeSubathon : pauseSubathon;
    action()
      .then((res) => {
        setSub(res);
        setSkewMs(clockSkewMs(res.serverNow));
        toast.success(sub.isPaused ? 'Đã tiếp tục subathon' : 'Đã tạm dừng subathon');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Thao tác thất bại');
        // Thất bại có thể do trạng thái phía máy chủ đã đổi khác trước đó (vd đã tự kết thúc) ->
        // tải lại để đồng bộ, tránh giao diện đứng yên ở trạng thái sai.
        load();
      })
      .finally(() => setToggling(false));
  };

  const handleEnd = () => {
    if (ending || !sub) return; // chặn bấm 2 lần
    if (!window.confirm('Kết thúc sớm subathon? Không thể hoàn tác.')) return;
    setEnding(true);
    endSubathon()
      .then((res) => {
        setSub(res);
        toast.success('Đã kết thúc subathon');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Kết thúc thất bại');
        // Cùng lý do như tạm dừng/tiếp tục: tải lại để đồng bộ với trạng thái thật trên máy chủ.
        load();
      })
      .finally(() => setEnding(false));
  };

  const openPreview = () => {
    window.open(overlayUrl, 'popup', 'width=700,height=320,top=100,left=100');
  };

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link overlay');
    } catch {
      toast.error('Trình duyệt chặn sao chép — hãy bôi đen link rồi copy tay.');
    }
  };

  const remaining = sub ? remainingAt(sub, now, skewMs) : 0;

  // Đồng hồ cục bộ vừa chạm 0 lúc đang chạy nhưng máy chủ chưa chắc đã kịp lưu Ended (job đọc
  // lười) -> tải lại một lần để xác nhận trạng thái mới, giống overlay livestream.
  useEffect(() => {
    if (isCounting && remaining <= 0 && !zeroReloadedRef.current) {
      zeroReloadedRef.current = true;
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCounting, remaining]);

  const inputCls =
    'h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Timer size={20} className="text-blue-600" />
        Đếm ngược subathon
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Đặt thời lượng ban đầu và tỉ lệ "cứ X Ca thêm 1 phút" rồi chèn đồng hồ đếm ngược lên
          phiên live. Mỗi lượt donate bạn nhận được sẽ tự cộng thêm giờ theo tỉ lệ này, tới khi
          chạm trần đã đặt.
        </p>

        {isActive && sub ? (
          <div className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
            <div className="truncate text-base font-semibold text-gray-800 dark:text-white">
              {sub.title}
            </div>
            <div className="mt-1 text-4xl font-extrabold tabular-nums text-blue-600">
              {formatHms(remaining)}
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {sub.isPaused ? 'Đang tạm dừng' : 'Đang chạy'} · đã cộng thêm{' '}
              {formatAdd(sub.addedSeconds)} · cứ{' '}
              {Number(sub.caPerMinute).toLocaleString('vi-VN')} Ca thêm 1 phút
            </div>

            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleTogglePause}
                disabled={toggling}
                className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {toggling ? 'Đang xử lý…' : sub.isPaused ? 'Tiếp tục' : 'Tạm dừng'}
              </button>
              <button
                type="button"
                onClick={handleEnd}
                disabled={ending}
                className="rounded-lg border border-red-400 px-4 py-2 text-base text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ending ? 'Đang xử lý…' : 'Kết thúc sớm'}
              </button>
            </div>

            {sub.recentAdds.length > 0 && (
              <div className="mt-3">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Lượt cộng giờ gần đây
                </div>
                <ul className="mt-1 flex flex-col gap-1">
                  {sub.recentAdds.map((add, idx) => {
                    const at = parseServerDate(add.at);
                    const time = at
                      ? at.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
                      : '';
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`${add.at}-${idx}`} className="flex items-baseline justify-between text-sm">
                        <span className="truncate text-gray-700 dark:text-gray-300">{add.name}</span>
                        <span className="shrink-0 font-semibold text-blue-600">
                          {formatAdd(add.seconds)} {time && `· ${time}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor="subathon-title" className="text-sm">
                Tên subathon
              </label>
              <input
                id="subathon-title"
                value={form.title}
                maxLength={80}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="VD: Subathon cuối tuần"
                className={inputCls}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="subathon-base" className="text-sm">
                Thời lượng ban đầu (phút)
              </label>
              <input
                id="subathon-base"
                type="number"
                min={BASE_MINUTES_MIN}
                max={BASE_MINUTES_MAX}
                step={1}
                value={form.baseMinutes}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    baseMinutes: toIntString(e.target.value, BASE_MINUTES_MIN, BASE_MINUTES_MAX),
                  }))
                }
                className={inputCls}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="subathon-rate" className="text-sm">
                Cứ bao nhiêu Ca thêm 1 phút
              </label>
              <input
                id="subathon-rate"
                type="number"
                min={1}
                step={1}
                value={form.caPerMinute}
                onChange={(e) =>
                  setForm((f) => ({ ...f, caPerMinute: toIntString(e.target.value, 1) }))
                }
                placeholder="VD: 10000"
                className={inputCls}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="subathon-max" className="text-sm">
                Trần tổng thời lượng (giờ)
              </label>
              <input
                id="subathon-max"
                type="number"
                min={MAX_HOURS_MIN}
                max={MAX_HOURS_MAX}
                step={1}
                value={form.maxHours}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    maxHours: toIntString(e.target.value, MAX_HOURS_MIN, MAX_HOURS_MAX),
                  }))
                }
                className={inputCls}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleStart}
                disabled={starting}
                className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {starting ? 'Đang bắt đầu…' : 'Bắt đầu'}
              </button>
            </div>
          </>
        )}

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">
            Link hiển thị đồng hồ ( Add Webpage URL trong phần mềm Stream )
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
          <button
            type="button"
            onClick={openPreview}
            className="mt-1 w-fit rounded-lg bg-[#39539E] px-4 py-2 text-base text-white"
          >
            Xem thử
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubathonSetting;
